import {
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import emojisState from '../state/emojisState';
import {useEffect, useRef} from 'react';
import firebaseApp from '../services/firebase';
import {useParams} from 'react-router-dom';
import {firebaseSessionIdState, firebaseUidState} from '../state/firebaseState';
import firebase from 'firebase/app';
import 'firebase/database';
import debounce from 'lodash.debounce';

const defaultRef = {
  addQueue: [],
  removeQueue: [],
  pendingPayload: null,
  db: undefined,
};

const useWatchEmojis = (enabled = true) => {
  const {timing} = useParams();
  const firebaseUid = useRecoilValue(firebaseUidState);
  const firebaseSessionId = useRecoilValue(firebaseSessionIdState);
  const resetEmojis = useResetRecoilState(emojisState);
  const setEmojis = useSetRecoilState(emojisState);

  const ref = useRef({...defaultRef});

  useEffect(() => {
    if (!firebaseUid || !enabled) {
      return;
    }

    ref.current.db = firebaseApp
    .database()
    .ref('room-reactions')
    .child(timing);

    const msgRef = ref.current.db
    .orderByChild('timestamp')
    .startAfter(Date.now());

    const childAdded = snapshot => {
      const val = {
        id: snapshot.key,
        ...snapshot.val()
      };

      if(val.userId !== firebaseUid) {
        ref.current.addQueue.push(val);
      }
    };

    msgRef.on('child_added', childAdded);

    return () => {
      msgRef.off('child_added', childAdded);
      ref.current = {...defaultRef};

      resetEmojis();
    };
  }, [timing, firebaseUid, enabled]);

  useEffect(() => {
    const t = setInterval(() => {

      if(ref.current.removeQueue.length === 0 && ref.current.addQueue.length === 0) {
        return;
      }

      const cleanupIds = ref.current.removeQueue.map(i => i.id);
      ref.current.removeQueue = [];

      setEmojis(old => ([
        ...old.filter(i => !cleanupIds.includes(i.id)),
        ...ref.current.addQueue.splice(0, 30)
      ]))
    }, 3000);

    return () => {
      clearInterval(t);
    };
  }, []);

  const save = ({id, ...obj}) => {
    ref.current.db.child(id).setWithPriority({
      ...obj,
    }, firebase.database.ServerValue.TIMESTAMP);
  };

  const savePayload = () => {
    if(ref.current.pendingPayload) {
      console.log('DBG MSG', 'SENT!!');
      save(ref.current.pendingPayload);

      ref.current.pendingPayload = null;
    }
  };

  const debounceSavePayload = debounce(savePayload, 100);

  const pushPayload = () => {
    if(!ref.current.pendingPayload) {
      return;
    }

    setEmojis(old => {
      return ([
        ...old.filter(i => i.id != ref.current.pendingPayload.id),
        {...ref.current.pendingPayload, isMe: true},
      ]);
    });
  }

  const send = (message) => {
    if(ref.current.pendingPayload?.message && message !== ref.current.pendingPayload?.message) {
      console.log('DBG MSG CANCEL');
      debounceSavePayload.cancel();
      pushPayload();
      savePayload();
    }

    if(message !== ref.current.pendingPayload?.message) {
      console.log('DBG MSG SET');
      ref.current.pendingPayload = {
        id: ref.current.db.push().key,
        message,
        count: 1,
        userId: firebaseUid,
        timestamp: firebase.database.ServerValue.TIMESTAMP,
      };
    }
    else {
      console.log('DBG MSG INCR');
      ref.current.pendingPayload.count++;
    }

    pushPayload();
    debounceSavePayload();
  };

  // Random emoji script
  // useEffect(() => {
  //   const t = setInterval(() => {
  //     ref.current.addQueue.push({
  //       id: Math.floor(Math.random() * 100000) + '-' + Math.floor(Math.random() * 100000),
  //       message: ["❤️","☁️","🎭","😍","👏","👋"][Math.floor(Math.random() * 3)],
  //       count: Math.floor(Math.random() * 10),
  //       userId: 0,
  //       timestamp: 1,
  //     });
  //   }, 30);
  //   return () => clearInterval(t);
  // }, []);

  return {
    send,
    dataRef: ref,
  };
}

export const GLOBAL_EMOJIS = [];
export const GLOBAL_CLEANUP_EMOJIS = [];

const useWatchEmojisXX = (enabled = true) => {
  const {timing} = useParams();
  const lastMessage = useRef('');
  const numOfClick = useRef(0);
  const firebaseUid = useRecoilValue(firebaseUidState);
  const firebaseSessionId = useRecoilValue(firebaseSessionIdState);
  const resetEmojis = useResetRecoilState(emojisState);
  const setEmojis = useSetRecoilState(emojisState);

  const emojiPayload = useRef({
    addQueue: [],
    removeQueue: [],
    data: null,
  });

  const onAdded = snapshot => {
    GLOBAL_EMOJIS.push(snapshot);
  };

  useEffect(() => {
    const t = setInterval(() => {
      const cleanupIds = GLOBAL_CLEANUP_EMOJIS.map(i => i.id);
      GLOBAL_CLEANUP_EMOJIS.splice(0, GLOBAL_CLEANUP_EMOJIS.length);
      setEmojis(old => ([
        ...old.filter(i => !cleanupIds.includes(i.id)),
        ...GLOBAL_EMOJIS.splice(0, 30)
      ]))
    }, 3000);

    return () => {
      clearInterval(t);
    };
  }, []);

  const saveToFirebase = message => {
    const messageObj = {
      userId: firebaseUid,
      timestamp: firebase.database.ServerValue.TIMESTAMP,
      message,
      numOfClick: numOfClick.current,
    };

    const newMessage = firebaseApp
      .database()
      .ref(`room-reactions/${timing}`)
      .push();
    newMessage.setWithPriority(
      messageObj,
      firebase.database.ServerValue.TIMESTAMP,
    );
    numOfClick.current = 0;
  };

  const debounceSave = debounce(saveToFirebase, 500);

  useEffect(() => {
    if (!firebaseUid || !enabled) {
      return;
    }

    let ref = firebaseApp
      .database()
      .ref('room-reactions')
      .child(timing)
      .orderByChild('timestamp')
      .startAfter(Date.now());

    const childAdded = snapshot => {
      const value = {id: snapshot.key, ...snapshot.val()};
      onAdded(value);
    };

    ref.on('child_added', childAdded);

    return () => {
      ref.off('child_added', childAdded);
      resetEmojis();
    };
  }, [timing, firebaseUid, enabled]);


  // save emoji
  return message => {
    if ((!firebaseSessionId, !enabled)) {
      return;
    }

    numOfClick.current++;

    if (lastMessage.current !== message) {
      lastMessage.current = message;
      return debounceSave(message);
    }

    return debounceSave(message);
  };
};

export default useWatchEmojis;
